/* eslint-disable camelcase */
import React, {
    ComponentProps, useRef, useState, useEffect, FC
} from "react";
import { PageLayout } from "@themuse/design-system";
import SearchInput from "components/header/SearchInput";
import { SearchResults } from "components/content/SearchResults";
import { useUserState } from "hooks/UserContext";
import { newsletterSubscribeRequest } from "utils/api";
import { useSearchResultsState } from "hooks/SearchResultsContext";
import { isBrowser } from "utils/browser";
import { BrowseLinks } from "./BrowseLinks";
import { SideBySideJDP } from "./SideBySideJDP";
import { FilterNav } from "../FilterNav/FilterNav";
import { DirectApplyModal, DirectApplyModalProps } from "./Modals";
import styles from "./SearchPageLayout.module.scss";

interface SearchPageLayoutProps {
    job?: string;
    page?: number;
}

/**
 * Renders the SearchPageLayout component, which provides the layout for the search page.
 * It includes a search input, filter navigation, and search results display.
 *
 * The component manages the state for clearing inputs, opening job details, and selecting jobs.
 * It automatically opens the first job if available and the screen width is larger than 992px.
 *
 * Utilizes feature flags to conditionally render a side-by-side job details pane.
 */
export const SearchPageLayout: FC<SearchPageLayoutProps> = ({ job = "", page = 1 }) => {
    const user = useUserState();
    const { results } = useSearchResultsState();
    const [clearInputs, setClearInputs] = useState(false);
    // Side by Side JDP State
    const [isOpen, setIsOpen] = useState<boolean>(false);
    // Direct Apply Modal State
    const [isDirectApplyOpen, setIsDirectApplyOpen] = useState<boolean>(false);
    const [selectedJob, setSelectedJob] = useState<string>(job);
    const isMobile = () => isBrowser() && window.innerWidth <= 992;
    const contentRef = useRef<HTMLElement>(null);
    const iframeRef = useRef<HTMLDivElement>(null);
    // Populated from the JDP iframe via the 'message" api.
    const [directApplyProps, setDirectApplyProps] = useState<Record<string, any>>(null);
    const [activePage, setActivePage] = React.useState<number>(page);

    /**
     * Reset clearInputs back to false after it's been set to true
     */
    useEffect(() => {
        if (clearInputs) {
            // Allow time for the clear action to be processed
            const timer = setTimeout(() => {
                setClearInputs(false);
                setActivePage(1);
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [clearInputs]);

    useEffect(() => {
        const onMessageListener = (event: MessageEvent) => {
            // Update the open state of the direct apply modal
            if (event.data === "openDirectApply") {
                setIsDirectApplyOpen(true);
            }
            // Iframe close listener wipe selected job, iframe url and close the iframe.
            if (event.data === "closeSideBySide") {
                setSelectedJob(null);
                setIsOpen(false);
            }
            // Update the props passed to the direct apply modal
            if (event.data?.name === "updateDirectApply" && event.data?.job) {
                setDirectApplyProps((prev) => ({
                    ...prev,
                    ...event.data.job
                }));
            }
        };

        window.addEventListener("message", onMessageListener);

        return () => {
            window.removeEventListener("message", onMessageListener);
        };
    }, []);

    /**
     * Auto open sidebar
     */
    useEffect(() => {
        // no auto open on mobile
        if (isMobile()) return;
        // auto open preselected job
        if (selectedJob) {
            setIsOpen(true);
        }
        // auto open calculated job
        if (!selectedJob) {
            const jobToUse = results[0]?.hit ?? null;
            const jobPath = jobToUse ? `${jobToUse.company.short_name}/${jobToUse.short_title}` : null;
            setSelectedJob(jobPath);
            setIsOpen(!!jobPath);
        }
    }, [results, job]);

    const pageLayoutProps: ComponentProps<typeof PageLayout> = {
        activePath: "/search",
        headerClassName: styles.pageHeader,
        footerClassName: styles.pageFooter,
        pageName: styles.pageWrapper,
        mainClassName: styles.main,
        showSubMenus: true,
        user,
        handleModalFormSubmit: newsletterSubscribeRequest
    };

    const onClear = () => {
        setClearInputs(true);
        setActivePage(1);
    };

    return (
        <PageLayout {...pageLayoutProps}>
            <section className={styles.header}>
                <SearchInput
                    clearInputs={clearInputs}
                    onClear={onClear}
                />
            </section>
            <FilterNav onClear={onClear} />
            <article ref={contentRef} className={`${styles.content} ${!selectedJob || !isOpen ? styles.closed : ""}`}>
                <SearchResults
                    onClear={onClear}
                    {...{
                        setIsOpen, setSelectedJob, selectedJob, activePage, setActivePage
                    }}
                />
                <BrowseLinks />
            </article>
            <SideBySideJDP iframeRef={iframeRef} isOpen={isOpen} selectedJob={selectedJob} />
            <DirectApplyModal
                // adding a key here to force this module to always re-instantiate when it receives new props.
                key={directApplyProps?.atsJobId ?? "none"}
                {...{
                    ...directApplyProps as DirectApplyModalProps,
                    isDirectApplyOpen,
                    setIsDirectApplyOpen
                }}
            />
        </PageLayout>
    );
};
SearchPageLayout.displayName = "SearchPageLayout";
